<script setup lang="ts" generic="T">
// Sierra's table: uppercase headers, 56 px rows (40 px when `dense`, for long admin
// lists), inside a rounded frame that scrolls sideways on narrow screens (`minWidth`,
// 640 px by default; 0 turns it off). Sortable columns, loading and empty states.
// Cells render `row[column.key]` unless a `cell-<key>` slot is given.
// - `interactive` rows are clickable and focusable, and emit `rowClick`.
// - `groupBy` puts rows under category rows (Sierra's tr.cat), in first-seen order.
// - `childrenOf` lists child rows, indented, under their parent.
import { computed, ref } from 'vue';
import type { TableColumn } from '../types.ts';
import EmptyState from './EmptyState.vue';
import Spinner from './Spinner.vue';

const props = withDefaults(
  defineProps<{
    columns: readonly TableColumn[];
    rows: readonly T[];
    rowKey: (row: T) => string;
    loading?: boolean;
    empty?: string;
    label?: string | undefined;
    interactive?: boolean;
    dense?: boolean;
    minWidth?: number;
    groupBy?: ((row: T) => string) | undefined;
    childrenOf?: ((row: T) => readonly T[]) | undefined;
  }>(),
  { loading: false, empty: 'Nothing here', interactive: false, dense: false, minWidth: 640 },
);
const emit = defineEmits<{ rowClick: [row: T] }>();

const sort = ref<{ key: string; dir: 1 | -1 } | null>(null);
const value = (row: T, key: string): unknown => (row as Record<string, unknown>)[key];
const sorted = computed(() => {
  const current = sort.value;
  if (current === null) return props.rows;
  return [...props.rows].sort((a, b) => {
    const [x, y] = [value(a, current.key), value(b, current.key)];
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * current.dir;
    return String(x ?? '').localeCompare(String(y ?? '')) * current.dir;
  });
});

type Line = { kind: 'group'; key: string; label: string } | { kind: 'row'; key: string; row: T; child: boolean };
const lines = computed<Line[]>(() => {
  const rowLines = (row: T): Line[] => [
    { kind: 'row', key: props.rowKey(row), row, child: false },
    ...(props.childrenOf?.(row) ?? []).map((child): Line => ({ kind: 'row', key: props.rowKey(child), row: child, child: true })),
  ];
  const groupBy = props.groupBy;
  if (groupBy === undefined) return sorted.value.flatMap(rowLines);
  const groups = new Map<string, T[]>();
  for (const row of sorted.value) {
    const name = groupBy(row);
    groups.set(name, [...(groups.get(name) ?? []), row]);
  }
  return [...groups].flatMap(([name, rows]): Line[] => [{ kind: 'group', key: `group:${name}`, label: name }, ...rows.flatMap(rowLines)]);
});

function toggle(column: TableColumn): void {
  if (column.sortable !== true) return;
  sort.value = sort.value?.key === column.key ? { key: column.key, dir: sort.value.dir === 1 ? -1 : 1 } : { key: column.key, dir: 1 };
}
const ariaSort = (column: TableColumn) => (sort.value?.key !== column.key ? 'none' : sort.value.dir === 1 ? 'ascending' : 'descending');
</script>

<template>
  <div class="itc-table-frame">
    <div class="itc-table-wrap">
      <table class="itc-table" :class="{ 'itc-table--dense': dense }" :style="minWidth > 0 ? { minWidth: `${minWidth}px` } : undefined" :aria-label="label" :aria-busy="loading">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key" :aria-sort="column.sortable ? ariaSort(column) : undefined" :class="{ 'is-right': column.align === 'right' }">
              <button v-if="column.sortable" type="button" class="itc-table__sort" @click="toggle(column)">
                {{ column.label }}<span aria-hidden="true">{{ sort?.key === column.key ? (sort.dir === 1 ? ' ↑' : ' ↓') : '' }}</span>
              </button>
              <template v-else>{{ column.label }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="line in lines" :key="line.key">
            <tr v-if="line.kind === 'group'" class="itc-table__cat">
              <th :colspan="columns.length" scope="colgroup">{{ line.label }}</th>
            </tr>
            <tr
              v-else
              :class="{ 'is-interactive': interactive, 'is-child': line.child }"
              :tabindex="interactive ? 0 : undefined"
              @click="interactive && emit('rowClick', line.row)"
              @keydown.enter="interactive && emit('rowClick', line.row)"
            >
              <td v-for="column in columns" :key="column.key" :class="{ 'is-right': column.align === 'right' }">
                <slot :name="`cell-${column.key}`" :row="line.row">{{ value(line.row, column.key) }}</slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div v-if="loading && rows.length === 0" class="itc-table-frame__state"><Spinner /></div>
    <EmptyState v-else-if="rows.length === 0" class="itc-table-frame__empty" :title="empty" />
  </div>
</template>

<style scoped>
.itc-table th.is-right,
.itc-table td.is-right {
  text-align: right;
}
.itc-table__sort {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
}
.itc-table tr.is-interactive {
  cursor: pointer;
}
.itc-table tr.is-interactive:hover td {
  background: var(--itc-panel-2);
}
.itc-table tr.is-interactive:focus-visible {
  outline: 2px solid var(--itc-accent);
  outline-offset: -2px;
}
.itc-table tr.is-child td:first-child {
  padding-left: 36px;
}
.itc-table tr.is-child td {
  height: 50px;
}
.itc-table .itc-table__cat th {
  padding: 14px 12px 6px;
  border-top: 1px solid var(--itc-line);
  border-bottom: 0;
  background: var(--itc-panel-2);
  font-weight: 700;
  letter-spacing: 0.12em;
}
.itc-table--dense td,
.itc-table--dense tr.is-child td {
  height: 40px;
  padding-block: 4px;
  font-size: 14px;
}
.itc-table-frame__state {
  display: flex;
  justify-content: center;
  padding: var(--itc-s5);
}
.itc-table-frame__empty {
  margin-top: var(--itc-s3);
}
.itc-panel .itc-table-frame__empty {
  margin: var(--itc-s3);
}
</style>
